// src/infrastructure/repositories/PubNubChatRepository.ts
//import PubNub from "pubnub";
import { pubnubService } from "../../services/PubnubService";

// ---- Define our own types (PubNub JS SDK does not export these) ----
type PNMessageEvent = {
  channel: string;
  message: any;
  publisher?: string;
  subscription?: string;
  timetoken: string | number;
};

type PNStatusEvent = {
  category: string;
  operation?: string;
  affectedChannels?: string[];
  subscribedChannels?: string[];
  currentTimetoken?: string | number;
  lastTimetoken?: string | number;
  [key: string]: unknown;
};

type PNPresenceEvent = {
  action: string;
  channel: string;
  uuid: string;
  occupancy: number;
  state?: any;
  timestamp: number;
};

// --------------------------------------------------------------------

export class PubNubChatRepository {
  init(userUuid: string) {
    pubnubService.init(userUuid);
  }

  subscribe(channels: string[]) {
    pubnubService.subscribe(channels);
  }

  addListener(callbacks: {
    message?: (msg: PNMessageEvent) => void;
    status?: (s: PNStatusEvent) => void;
    presence?: (p: PNPresenceEvent) => void;
  }) {
    pubnubService.addListener({
      message: callbacks.message ? (msg: unknown) => callbacks.message?.(msg as PNMessageEvent) : undefined,
      status: callbacks.status ? (s: unknown) => callbacks.status?.(s as PNStatusEvent) : undefined,
      presence: callbacks.presence ? (p: unknown) => callbacks.presence?.(p as PNPresenceEvent) : undefined,
    });
  }

  async sendMessage(
    theirUuid: string,
    myUuid: string,
    text: string,
    extra: Record<string, any> = {}
  ) {
    if (!pubnubService.pubnub) throw new Error("PubNub not initialized");

    const channel = `${theirUuid}.${myUuid}`;
    const payload = { text, ...extra };

    try {
      const result = await pubnubService.pubnub.publish({
        channel,
        message: payload,
      });

      return { ok: true, result };
    } catch (err) {
      console.error("PubNub publish error:", err);
      return { ok: false, error: err };
    }
  }

  async deleteMessages(
  channel: string,
  start?: number,
  end?: number
) {
  if (!pubnubService.pubnub) {
    throw new Error("PubNub not initialized");
  }

  try {
    const resp = await (pubnubService.pubnub as any).deleteMessages({
      channel,
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
    });

    return { ok: true, resp };
  } catch (err) {
    console.error("deleteMessages error:", err);
    return { ok: false, error: err };
  }
}

async deleteAllMessages(channel: string) {
  if (!pubnubService.pubnub) {
    throw new Error("PubNub not initialized");
  }

  try {
    const resp = await (pubnubService.pubnub as any).deleteMessages({
      channel,
    });

    return { ok: true, resp };
  } catch (err) {
    console.error("deleteAllMessages error:", err);
    return { ok: false, error: err };
  }
}


  async getConversation(channel: string, count: number = 50) {
    if (!pubnubService.pubnub) throw new Error("PubNub not initialized");

    try {
      const resp = await pubnubService.pubnub.fetchMessages({
        channels: [channel],
        count,
      });

      const messages = resp.channels?.[channel] || [];

      const chats = messages.map((m: any) => {
        const msg = m.message || {};
        return {
          id: String(m.timetoken),
          user_id: msg.senderId || msg.from || "unknown",
          message: msg.text ?? JSON.stringify(msg),
          timestamp: Number(String(m.timetoken).slice(0, -6)),
          raw: m,
        };
      });

      return { ok: true, chats };
    } catch (err) {
      console.error("fetchMessages error:", err);
      return { ok: false, error: err };
    }
  }

  async activeFriends(receivingChannels: string[]) {
    if (!pubnubService.pubnub) throw new Error("PubNub not initialized");
    if (receivingChannels.length === 0) return [];

    try {
      const resp = await pubnubService.pubnub.hereNow({
        channels: receivingChannels,
        includeUUIDs: true,
      });

      const active: string[] = [];

      for (const ch of receivingChannels) {
        const data = resp.channels?.[ch];
        if (data?.occupants?.length > 0) {
          active.push(ch);
        }
      }

      return active;
    } catch (err) {
      console.error("hereNow error:", err);
      return [];
    }
  }

  async getUnreadCounts(receivingChannels: string[], channelTimetokens: number[]) {
    if (!pubnubService.pubnub) throw new Error("PubNub not initialized");

    const tokensAsString = channelTimetokens.map((t) => String(t));

    try {
      const resp = await pubnubService.pubnub.messageCounts({
        channels: receivingChannels,
        channelTimetokens: tokensAsString,
      });

      return resp.channels || {};
    } catch (err) {
      console.error("messageCounts error:", err);
      return {};
    }
  }

  unsubscribeAll() {
    try {
      pubnubService.pubnub?.unsubscribeAll();
    } catch (err) {
      console.warn("unsubscribeAll error:", err);
    }
  }
}

export const pubnubChatRepository = new PubNubChatRepository();
